// Widget-editor Drawer: subject is deep-linked as `?widget=<id|new>` so refresh
// reopens it and close strips it. A real query builder over the closed metrics
// registry: metric, visualization, group-by, the metric's typed filters, bucket,
// and a live preview rendered from the draft through the same per-tile
// `metrics:query` path the board uses. The server stays the validation
// authority — an invalid draft surfaces its VALIDATION_FAILED in the preview,
// and Save is a plain config PATCH.

import {
  useDeferredValue,
  useMemo,
  useState,
  type SyntheticEvent,
} from "react";

import {
  defaultLayoutSize,
  defaultWidgetTitle,
  METRIC_METADATA,
  METRIC_OPTIONS,
  METRIC_VISUALIZATIONS,
  newWidgetId,
  TIME_SHAPED_VISUALIZATIONS,
  VISUALIZATION_LABELS,
  type DashboardComparisonMode,
  type DashboardWidget,
  type DashboardWidgetVisualization,
  type MetricFilters,
} from "../lib/dashboardWidgets";
import type { MarkerDisplayOption } from "../lib/useDashboardMarkerNames";
import type { MarkerDisplayNames } from "../lib/dashboardMetrics";
import type {
  MetricBucket,
  MetricDimension,
  MetricName,
  TimeWindow,
} from "../lib/dashboardMetrics";
import {
  Button,
  Drawer,
  Inline,
  InlineError,
  MultiSelect,
  Section,
  Select,
  Stack,
  TextInput,
} from "../ui";
import { MetricWidgetBody } from "./DashboardWidgetTile";

type FilterKey = keyof MetricFilters;

const FILTER_LABELS: Record<FilterKey, string> = {
  agent_id: "Agent ID",
  agent_version_id: "Agent version ID",
  marker_id: "Marker ID",
  persona_id: "Persona ID",
  scenario_id: "Scenario ID",
  scenario_set_id: "Scenario set ID",
  batch_id: "Batch ID",
  transcript_status: "Transcript status",
  fail_class: "Fail class",
  source: "Source",
  event_type: "Event type",
  modality: "Modality",
  transport: "Transport",
  initiator: "Initiator",
  provider: "Provider",
  name: "Span name",
  kind: "Span kind",
};

// Closed filter enums, mirrored from `marker.schemas` (docs/04 §11). The
// generated client carries these as types only; the server rejects anything
// off-enum with VALIDATION_FAILED.
const FILTER_ENUM_OPTIONS: Partial<Record<FilterKey, readonly string[]>> = {
  transcript_status: ["pending", "processing", "ready", "failed"],
  fail_class: ["harness_fail", "agent_fail"],
  source: ["live", "simulated"],
  event_type: [
    "simulated_voice_minutes",
    "simulated_conversation",
    "submitted_voice_minutes",
    "submitted_conversation",
    "ingested_voice_minutes",
    "ingested_transcript",
  ],
  modality: ["voice", "text"],
  transport: ["http", "websocket", "telephony", "webrtc"],
  initiator: ["marker", "agent"],
  kind: ["UNSPECIFIED", "INTERNAL", "SERVER", "CLIENT", "PRODUCER", "CONSUMER"],
};

const BUCKET_OPTIONS: readonly { value: string; label: string }[] = [
  { value: "auto", label: "Auto" },
  { value: "hour", label: "Hourly" },
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
];

interface WidgetDraft {
  title: string;
  metric: MetricName;
  visualization: DashboardWidgetVisualization;
  groupBy: MetricDimension[];
  filters: Partial<MetricFilters>;
  bucket: MetricBucket | null;
  comparison: DashboardComparisonMode | null;
}

function draftFromWidget(widget: DashboardWidget | undefined): WidgetDraft {
  if (!widget) {
    return {
      title: "",
      metric: "marker_pass_rate",
      visualization: "timeseries",
      groupBy: [],
      filters: {},
      bucket: null,
      comparison: null,
    };
  }
  const filters: Partial<MetricFilters> = {};
  for (const [key, value] of Object.entries(widget.query.filters ?? {})) {
    if (value !== null) {
      filters[key as FilterKey] = value as never;
    }
  }
  return {
    title: widget.title,
    metric: widget.query.metric,
    visualization: widget.visualization,
    groupBy: [...(widget.query.group_by ?? [])],
    filters,
    bucket: widget.query.bucket ?? null,
    comparison: widget.comparison,
  };
}

function widgetFromDraft(
  draft: WidgetDraft,
  existing: DashboardWidget | undefined,
): DashboardWidget {
  const metricMetadata = METRIC_METADATA[draft.metric];
  const filters: Partial<MetricFilters> = {};
  for (const key of metricMetadata.filters) {
    const value = draft.filters[key];
    if (value !== undefined && value !== null && value !== "") {
      filters[key] = value as never;
    }
  }
  const size = defaultLayoutSize(draft.visualization);
  return {
    id: existing?.id ?? newWidgetId(),
    title:
      draft.title.trim() ||
      defaultWidgetTitle(draft.metric, draft.visualization, draft.groupBy),
    visualization: draft.visualization,
    query: {
      metric: draft.metric,
      group_by: draft.groupBy,
      filters: filters as MetricFilters,
      bucket: draft.bucket,
    },
    layout: existing?.layout ?? { x: 0, y: 0, ...size },
    comparison: draft.visualization === "stat" ? draft.comparison : null,
  };
}

export interface DashboardWidgetEditorProps {
  agentId?: string;
  error: string | null;
  isOpen: boolean;
  isPending: boolean;
  markerDisplayNames: MarkerDisplayNames;
  markerOptions: readonly MarkerDisplayOption[];
  onCancel: () => void;
  onSave: (widget: DashboardWidget) => void;
  widget: DashboardWidget | undefined;
  window: TimeWindow;
}

export function DashboardWidgetEditor(props: DashboardWidgetEditorProps) {
  const subjectKey = props.widget?.id ?? "new";
  return (
    <DashboardWidgetEditorForm
      key={`${subjectKey}:${props.isOpen ? "open" : "closed"}`}
      {...props}
    />
  );
}

function DashboardWidgetEditorForm({
  agentId,
  error,
  isOpen,
  isPending,
  markerDisplayNames,
  markerOptions,
  onCancel,
  onSave,
  widget,
  window,
}: DashboardWidgetEditorProps) {
  const [draft, setDraft] = useState<WidgetDraft>(() =>
    draftFromWidget(widget),
  );

  const metricMetadata = METRIC_METADATA[draft.metric];
  const isTimeShaped = TIME_SHAPED_VISUALIZATIONS.has(draft.visualization);
  const groupableDimensions = metricMetadata.dimensions.filter(
    (dimension) => !metricMetadata.pinnedGroupBy?.includes(dimension),
  );
  const canGroup =
    !metricMetadata.pinnedGroupBy &&
    (!isTimeShaped || draft.visualization === "stacked_bar");

  const onMetricChange = (metric: MetricName) => {
    const nextMetricMetadata = METRIC_METADATA[metric];
    setDraft((current) => ({
      ...current,
      metric,
      visualization: nextMetricMetadata.allowedVisualizations.includes(
        current.visualization,
      )
        ? current.visualization
        : (nextMetricMetadata.allowedVisualizations[0] ?? "table"),
      groupBy: [
        ...(nextMetricMetadata.pinnedGroupBy ??
          nextMetricMetadata.requiredDimensions),
      ] as MetricDimension[],
      filters: {},
      bucket: null,
    }));
  };

  const onVisualizationChange = (
    visualization: DashboardWidgetVisualization,
  ) => {
    setDraft((current) => ({
      ...current,
      visualization,
      // stat and single-series time visualization read ungrouped data; keep required
      // dimensions (the category axis) and pinned axes.
      groupBy:
        visualization === "stat" ||
        (TIME_SHAPED_VISUALIZATIONS.has(visualization) &&
          visualization !== "stacked_bar")
          ? ([
              ...(metricMetadata.pinnedGroupBy ??
                metricMetadata.requiredDimensions),
            ] as MetricDimension[])
          : current.groupBy,
    }));
  };

  const previewWidget = useMemo(
    () => widgetFromDraft(draft, widget),
    [draft, widget],
  );
  const deferredPreview = useDeferredValue(previewWidget);

  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSave(widgetFromDraft(draft, widget));
  };

  return (
    <Drawer
      ariaLabel={widget ? "Edit widget" : "Add widget"}
      isOpen={isOpen}
      size="lg"
      onDismiss={onCancel}
    >
      <form onSubmit={submit}>
        <Stack gap={5}>
          <TextInput
            label="Title"
            placeholder={defaultWidgetTitle(
              draft.metric,
              draft.visualization,
              draft.groupBy,
            )}
            value={draft.title}
            onChange={(event) => {
              const title = event.currentTarget.value;
              setDraft((current) => ({ ...current, title }));
            }}
          />
          <Select
            label="Metric"
            value={draft.metric}
            onChange={(event) => {
              onMetricChange(event.currentTarget.value as MetricName);
            }}
          >
            {METRIC_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Select
            label="Visualization"
            value={draft.visualization}
            onChange={(event) => {
              onVisualizationChange(
                event.currentTarget.value as DashboardWidgetVisualization,
              );
            }}
          >
            {METRIC_VISUALIZATIONS.filter((visualization) =>
              metricMetadata.allowedVisualizations.includes(visualization),
            ).map((visualization) => (
              <option key={visualization} value={visualization}>
                {VISUALIZATION_LABELS[visualization]}
              </option>
            ))}
          </Select>
          {canGroup ? (
            <MultiSelect
              label="Group by (up to 2)"
              options={groupableDimensions.map((dimension) => ({
                label: dimension.replaceAll("_", " "),
                value: dimension,
              }))}
              value={draft.groupBy.filter(
                (dimension) =>
                  !metricMetadata.pinnedGroupBy?.includes(dimension),
              )}
              onChange={(next) => {
                const required = metricMetadata.requiredDimensions.filter(
                  (dimension) => !next.includes(dimension),
                );
                const groupBy = [...required, ...next].slice(
                  0,
                  2,
                ) as MetricDimension[];
                setDraft((current) => ({ ...current, groupBy }));
              }}
            />
          ) : null}
          <Section hairline heading="Filters">
            <Stack gap={4}>
              {metricMetadata.filters.map((key) => (
                <FilterField
                  key={key}
                  filterKey={key}
                  markerOptions={markerOptions}
                  value={draft.filters[key]}
                  onChange={(value) => {
                    setDraft((current) => ({
                      ...current,
                      filters: { ...current.filters, [key]: value },
                    }));
                  }}
                />
              ))}
            </Stack>
          </Section>
          {isTimeShaped && metricMetadata.bucketable ? (
            <Select
              label="Bucket"
              value={draft.bucket ?? "auto"}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setDraft((current) => ({
                  ...current,
                  bucket: value === "auto" ? null : (value as MetricBucket),
                }));
              }}
            >
              {BUCKET_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          ) : null}
          {draft.visualization === "stat" ? (
            <Select
              label="Compare vs previous period"
              value={draft.comparison ?? "none"}
              onChange={(event) => {
                const comparison =
                  event.currentTarget.value === "none"
                    ? null
                    : (event.currentTarget.value as DashboardComparisonMode);
                setDraft((current) => ({ ...current, comparison }));
              }}
            >
              <option value="previous_period">Previous period</option>
              <option value="none">None</option>
            </Select>
          ) : null}
          <Section hairline heading="Preview">
            <MetricWidgetBody
              agentId={agentId}
              markerDisplayNames={markerDisplayNames}
              widget={deferredPreview}
              window={window}
            />
          </Section>
          {error ? <InlineError>{error}</InlineError> : null}
          <Inline>
            <Button disabled={isPending} type="submit">
              {widget ? "Save widget" : "Add widget"}
            </Button>
            <Button
              disabled={isPending}
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Inline>
        </Stack>
      </form>
    </Drawer>
  );
}

function FilterField({
  filterKey,
  markerOptions,
  value,
  onChange,
}: {
  filterKey: FilterKey;
  markerOptions: readonly MarkerDisplayOption[];
  value: unknown;
  onChange: (value: string | null) => void;
}) {
  const label = FILTER_LABELS[filterKey];
  const enumOptions = FILTER_ENUM_OPTIONS[filterKey];
  const current = typeof value === "string" ? value : "";
  if (filterKey === "marker_id") {
    const hasCurrentOption = markerOptions.some(
      (option) => option.value === current,
    );
    return (
      <Select
        label="Marker"
        value={current}
        onChange={(event) => {
          onChange(event.currentTarget.value || null);
        }}
      >
        <option value="">Any marker</option>
        {current && !hasCurrentOption ? (
          <option value={current}>{current}</option>
        ) : null}
        {markerOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    );
  }
  if (enumOptions) {
    return (
      <Select
        label={label}
        value={current}
        onChange={(event) => {
          onChange(event.currentTarget.value || null);
        }}
      >
        <option value="">Any</option>
        {enumOptions.map((option) => (
          <option key={option} value={option}>
            {option.replaceAll("_", " ")}
          </option>
        ))}
      </Select>
    );
  }
  return (
    <TextInput
      label={label}
      value={current}
      onChange={(event) => {
        onChange(event.currentTarget.value || null);
      }}
    />
  );
}
